// tasksApi — typed fetchers for /v1/admin/tasks (Location Tasks Phase 2).
// Backend: apps/tenant-api/src/tasks.ts.
import { apiFetch } from "../../lib/api-client.js";

export interface TaskRow {
  id: string;
  name: string;
  description_short: string | null;
  /** TipTap ProseMirror JSON, stored as string. Front-end editor parses
   *  on load; renderer serialises back on save. */
  instruction_doc: string | null;
  // evidence (Step 2)
  requires_text: boolean;
  requires_camera: boolean;
  requires_file: boolean;
  // verification (Step 1)
  verify_device: boolean;
  verify_face: boolean;
  verify_geo: boolean;
  // finalisation (Step 3)
  requires_signature: boolean;
  // skip policy
  allow_skip: boolean;
  created_by_user_id: string | null;
  created_at: number;
  updated_at: number;
  status: "active" | "archived";
  /** Number of currently-active assignments (across all locations). */
  assignment_count: number;
}

export interface TasksListResponse {
  items: TaskRow[];
  total: number;
  correlation_id: string | null;
}

export interface TaskBody {
  name?: string;
  description_short?: string | null;
  instruction_doc?: string | null;
  requires_text?: boolean;
  requires_camera?: boolean;
  requires_file?: boolean;
  verify_device?: boolean;
  verify_face?: boolean;
  verify_geo?: boolean;
  requires_signature?: boolean;
  allow_skip?: boolean;
  status?: "active" | "archived";
}

export class TasksApiError extends Error {
  status: number; code: string; correlationId: string | null; upstreamMessage?: string;
  constructor(status: number, code: string, correlationId: string | null, upstreamMessage?: string) {
    super(code); this.status = status; this.code = code; this.correlationId = correlationId; this.upstreamMessage = upstreamMessage;
  }
}

export function friendlyTasksError(err: unknown): string {
  if (err instanceof TasksApiError) {
    if (err.status === 401) return "登入狀態已逾時，請重新登入後再試。";
    if (err.status === 403) return "您目前的角色不能管理任務。";
    if (err.status === 404) return "找不到此任務。";
    if (err.code === "name_required") return "任務名稱為必填。";
    if (err.code === "name_too_long") return "任務名稱過長（最長 200 字）。";
    if (err.code === "instruction_doc_too_large") return "任務說明內容過大（最大 200 KB），請精簡。";
    if (err.code === "tenant_id_required")
      return "尚未選定操作的單位，請先到「平台 / 租戶」選擇要進入的單位。";
    if (err.code === "no_fields_to_update") return "沒有要更新的欄位。";
    if (err.code === "invalid_status") return "狀態值不正確。";
    return `操作失敗（HTTP ${err.status}・code=${err.code}・cid=${err.correlationId ?? "n/a"}）。`;
  }
  return "連線中斷，請檢查網路後再試。";
}

async function asError(r: Response): Promise<TasksApiError> {
  let code = "request_failed"; let cid: string | null = r.headers.get("x-correlation-id"); let msg: string | undefined;
  try {
    const body = (await r.json()) as { error?: string; correlation_id?: string; message?: string };
    code = body.error ?? code; cid = body.correlation_id ?? cid; msg = body.message;
  } catch {}
  return new TasksApiError(r.status, code, cid, msg);
}

function tenantQuery(tenantId: string | null | undefined): string {
  return tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : "";
}

export interface TasksQuery {
  tenantId?: string | null;
  q?: string | null;
  status?: "active" | "archived" | "all" | null;
  limit?: number | null;
  offset?: number | null;
}

export async function listTasks(params: TasksQuery = {}): Promise<TasksListResponse> {
  const qs = new URLSearchParams();
  if (params.tenantId) qs.set("tenant_id", params.tenantId);
  if (params.q) qs.set("q", params.q);
  if (params.status) qs.set("status", params.status);
  if (params.limit != null) qs.set("limit", String(params.limit));
  if (params.offset != null) qs.set("offset", String(params.offset));
  const r = await apiFetch(`/v1/admin/tasks${qs.toString() ? `?${qs}` : ""}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as TasksListResponse;
}

export async function getTask(id: string, tenantId: string | null = null): Promise<TaskRow> {
  const r = await apiFetch(`/v1/admin/tasks/${encodeURIComponent(id)}${tenantQuery(tenantId)}`, { credentials: "include" });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as TaskRow;
}

export async function createTask(body: TaskBody, tenantId: string | null = null): Promise<{ ok: true; id: string }> {
  const r = await apiFetch(`/v1/admin/tasks${tenantQuery(tenantId)}`, {
    method: "POST", headers: { "content-type": "application/json" },
    body: JSON.stringify(body), credentials: "include",
  });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { ok: true; id: string };
}

export async function patchTask(id: string, body: TaskBody, tenantId: string | null = null): Promise<{ ok: true; id: string }> {
  const r = await apiFetch(`/v1/admin/tasks/${encodeURIComponent(id)}${tenantQuery(tenantId)}`, {
    method: "PATCH", headers: { "content-type": "application/json" },
    body: JSON.stringify(body), credentials: "include",
  });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { ok: true; id: string };
}

export async function archiveTask(id: string, tenantId: string | null = null): Promise<{ ok: true }> {
  const r = await apiFetch(`/v1/admin/tasks/${encodeURIComponent(id)}${tenantQuery(tenantId)}`, {
    method: "DELETE", credentials: "include",
  });
  if (!r.ok) throw await asError(r);
  return (await r.json()) as { ok: true };
}
